import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "../lib/api";
import { brl } from "../lib/format";
import { toast } from "sonner";
import {
  ArrowLeft, FilePdf, CurrencyDollar, ShoppingCart, VideoCamera, CalendarBlank,
  TrendUp, Fire, Crown, WhatsappLogo,
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const PIE_COLORS = ["#AB0000", "#E11818", "#FFFDDE", "#E1F2E8", "#FFDFD3", "#EDE0BC", "#F7B1A5", "#D0E9C6"];
const CHART_TOOLTIP_STYLE = { background: "#FFF", border: "2px solid #0A0A0A", boxShadow: "4px 4px 0 #0A0A0A", borderRadius: 0 };
const CHART_TICK_STYLE = { fill: "#0A0A0A", fontSize: 11, fontWeight: 700 };
const CHART_TICK_SMALL = { fill: "#0A0A0A", fontSize: 11 };
const LEGEND_STYLE = { fontSize: 11, fontWeight: 700 };
const BRL_TICK = (v) => `R$${v}`;

export default function LoyaltyDetail() {
  const { handle } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/loyalty/${encodeURIComponent(handle)}`);
      setD(data);
    } catch {
      toast.error("Cliente não encontrado");
      nav("/loyalty");
    } finally {
      setLoading(false);
    }
  }, [handle, nav]);

  useEffect(() => { load(); }, [load]);

  const openPdf = (kind) => {
    // Cache-buster prevents browsers from reusing a stale PDF after edits/deletes.
    const url = `${API}/loyalty/${encodeURIComponent(handle)}/report.pdf?kind=${kind}&_=${Date.now()}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (loading || !d) return <div className="p-8 font-bold">Carregando…</div>;

  const paidRate = d.total_spent > 0 ? Math.round((d.total_paid / d.total_spent) * 100) : 0;
  const waLink = d.customer?.whatsapp
    ? `https://wa.me/${d.customer.whatsapp.replace(/\D/g, "")}`
    : null;

  return (
    <div data-testid="loyalty-detail-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button data-testid="back-to-loyalty" onClick={() => nav("/loyalty")} className="inline-flex items-center gap-2 font-bold text-sm hover:underline">
          <ArrowLeft weight="bold" size={16} /> Voltar
        </button>
        <div className="flex gap-2 flex-wrap">
          {waLink && (
            <a data-testid="whatsapp-link" href={waLink} target="_blank" rel="noopener noreferrer" className="btn-mint">
              <WhatsappLogo weight="bold" size={16} /> WhatsApp
            </a>
          )}
          <button data-testid="pdf-summary-btn" onClick={() => openPdf("summary")} className="btn-white">
            <FilePdf weight="bold" size={16} /> PDF Resumo
          </button>
          <button data-testid="pdf-full-btn" onClick={() => openPdf("full")} className="btn-pink">
            <FilePdf weight="bold" size={16} /> PDF Completo
          </button>
        </div>
      </div>

      <header className="card-brutal p-6 bg-brand-purple text-white">
        <p className="text-[11px] font-bold uppercase tracking-widest">Cliente</p>
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black">{d.handle}</h1>
            {d.customer?.full_name && <p className="text-white font-bold mt-1">{d.customer.full_name}</p>}
            <p className="text-sm text-white/80">
              {[d.customer?.city, d.customer?.state].filter(Boolean).join("/") || "sem endereço"}
              {d.customer?.email ? ` · ${d.customer.email}` : ""}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {d.orders_count >= 5 && (
              <span className="badge-brutal !bg-brand-yellow !text-brand-ink"><Fire weight="bold" size={12} /> Frequente</span>
            )}
            {d.total_spent >= 1000 && (
              <span className="badge-brutal !bg-brand-yellow !text-brand-ink"><Crown weight="bold" size={12} /> Ouro</span>
            )}
            {d.days_since_last !== null && d.days_since_last <= 30 && (
              <span className="badge-brutal !bg-brand-mint !text-brand-ink">Ativo</span>
            )}
          </div>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat testid="ld-total" label="Total gasto" value={brl(d.total_spent)} bg="bg-brand-mint" icon={CurrencyDollar} />
        <Stat testid="ld-paid" label={`Pago (${paidRate}%)`} value={brl(d.total_paid)} bg="bg-white" icon={CurrencyDollar} />
        <Stat testid="ld-pending" label="Pendente" value={brl(d.total_pending)} bg="bg-brand-peach" icon={CurrencyDollar} />
        <Stat testid="ld-orders" label="Pedidos" value={d.orders_count} bg="bg-brand-yellow" icon={ShoppingCart} />
        <Stat testid="ld-lives" label="Lives" value={d.lives_count} bg="bg-white" icon={VideoCamera} />
        <Stat testid="ld-avg" label="Ticket médio" value={brl(d.avg_ticket)} bg="bg-brand-pink text-white" icon={TrendUp} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <MiniStat testid="ld-first" label="Primeiro pedido" value={(d.first_order || "").slice(0, 10) || "—"} icon={CalendarBlank} />
        <MiniStat testid="ld-last" label="Último pedido" value={(d.last_order || "").slice(0, 10) || "—"} icon={CalendarBlank} />
        <MiniStat
          testid="ld-days"
          label="Dias sem comprar"
          value={d.days_since_last !== null ? `${d.days_since_last}` : "—"}
          icon={CalendarBlank}
        />
        <MiniStat
          testid="ld-peak"
          label="Mês de pico"
          value={d.peak_month ? `${d.peak_month.month}` : "—"}
          hint={d.peak_month ? brl(d.peak_month.total) : ""}
          icon={Fire}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-brutal p-5 lg:col-span-2">
          <h2 className="font-heading text-lg font-black mb-3">Gastos por mês</h2>
          {d.monthly.length === 0 ? (
            <p className="text-brand-ink2 py-8 text-center">Sem histórico ainda.</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#0A0A0A22" />
                  <XAxis dataKey="month" tick={CHART_TICK_STYLE} />
                  <YAxis tick={CHART_TICK_SMALL} tickFormatter={BRL_TICK} />
                  <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v) => brl(v)} />
                  <Bar dataKey="total" fill="#AB0000" stroke="#1A1A1A" strokeWidth={2} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="card-brutal p-5">
          <h2 className="font-heading text-lg font-black mb-3">Departamentos</h2>
          {d.departments.length === 0 ? (
            <p className="text-brand-ink2 py-8 text-center">Sem dados.</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={d.departments}
                    dataKey="total"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    stroke="#0A0A0A"
                    strokeWidth={2}
                  >
                    {d.departments.map((dep, i) => (
                      <Cell key={dep.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v) => brl(v)} />
                  <Legend wrapperStyle={LEGEND_STYLE} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="card-brutal overflow-x-auto">
        <div className="p-4 border-b-2 border-brand-ink bg-brand-yellow flex items-center justify-between">
          <h2 className="font-heading text-lg font-black">Histórico completo · {d.orders.length} pedidos</h2>
        </div>
        <table className="tbl w-full">
          <thead>
            <tr>
              <th>Data</th>
              <th>Live</th>
              <th>Código</th>
              <th>Descrição</th>
              <th>Departamento</th>
              <th className="text-right">Qtd</th>
              <th className="text-right">Subtotal</th>
              <th className="text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {d.orders.map((o) => (
              <tr key={o.id} data-testid={`ld-order-${o.id}`}>
                <td className="whitespace-nowrap">{o.live_date}</td>
                <td>{o.live_name}</td>
                <td className="font-bold">{o.product_code}</td>
                <td>{o.description}</td>
                <td>{o.department_name || <span className="text-brand-ink2">—</span>}</td>
                <td className="text-right">{o.quantity}</td>
                <td className="text-right font-bold">{brl(o.subtotal)}</td>
                <td className="text-right">
                  {o.paid ? <span className="badge-brutal bg-brand-success text-white">Pago</span>
                          : <span className="badge-brutal bg-brand-peach">Pendente</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, bg, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className={`card-brutal p-4 ${bg}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-bold uppercase tracking-wider">{label}</p>
        <Icon weight="bold" size={14} />
      </div>
      <p className="font-heading text-xl font-black break-all">{value}</p>
    </div>
  );
}

function MiniStat({ label, value, hint, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="card-brutal p-3 bg-white flex items-center gap-3">
      <div className="w-10 h-10 bg-brand-yellow border-2 border-brand-ink flex items-center justify-center flex-shrink-0">
        <Icon weight="bold" size={16} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-widest text-brand-ink2">{label}</p>
        <p className="font-heading font-black leading-tight">{value}</p>
        {hint && <p className="text-[10px] text-brand-ink2">{hint}</p>}
      </div>
    </div>
  );
}
